import {isNil, isObject} from 'lodash';
import {Primitive} from 'type-fest';

import {AnyRecord} from 'types';

export type CleanEmptyValuesOptions = {
  nullableList?: string[];
};

export function cleanEmptyValues<T extends AnyRecord>(obj: T, options?: CleanEmptyValuesOptions) {
  return Object.keys(obj).reduce((acc, key) => {
    let value = obj[key];

    if (isObject(value) && !Array.isArray(value)) {
      value = cleanEmptyValues(value, options);
    }

    if (Array.isArray(value)) {
      value = value.map(item => {
        return isObject(item) && !Array.isArray(item) ? cleanEmptyValues(item, options) : item;
      });
    }

    if (!isNil(value) || options?.nullableList?.includes(key)) {
      acc[key as keyof T] = value;
    }

    return acc;
  }, {} as Partial<T>);
}

// Also cleans {}, []
export function cleanEmptyValuesStrict<T extends Primitive | AnyRecord | any[]>(value: T): T | undefined {
  if (Array.isArray(value)) {
    const cleanedArray = value
      .map(cleanEmptyValuesStrict)
      .filter(v => !isNil(v) && !(Array.isArray(v) && v.length === 0) && !(isObject(v) && Object.keys(v).length === 0));
    return cleanedArray.length > 0 ? (cleanedArray as T) : undefined;
  }

  if (isObject(value)) {
    const cleanedObj: AnyRecord = {};

    for (const [key, val] of Object.entries(value)) {
      const cleanedVal = cleanEmptyValuesStrict(val);

      if (
        !isNil(cleanedVal) &&
        !(Array.isArray(cleanedVal) && cleanedVal.length === 0) &&
        !(isObject(cleanedVal) && Object.keys(cleanedVal).length === 0)
      ) {
        cleanedObj[key] = cleanedVal;
      }
    }

    return Object.keys(cleanedObj).length > 0 ? (cleanedObj as T) : undefined;
  }

  return value;
}
